/**
 * Find a company on Companies House, pick one of its accounts filings, import it.
 *
 * The server fetches the filing and runs the same import an uploaded PDF does
 * (`importFromCompaniesHouse` in the store), so progress, warnings and the
 * resulting model are the upload's. Shown only where the deployment has a
 * Companies House key.
 */

import { useEffect, useState } from 'react';
import {
  companiesHouseConfigured,
  companyAccounts,
  searchCompanies,
  useFinancialModelV2Store,
  type AccountsFiling,
  type CompanyHit,
} from '../../store/financial-model-v2';
import { Button } from './ui/Button';
import { ReadAfresh } from './ui/ReadAfresh';

/** Common sectors as their SIC codes - the advanced search filters on codes, not words. */
export const SECTORS: { label: string; codes: string }[] = [
  { label: 'Any sector', codes: '' },
  { label: 'Software and SaaS', codes: '58290,62011,62012,62020,62090,63110' },
  { label: 'Hotels and restaurants', codes: '55100,56101,56102,56103,56302' },
  { label: 'Retail', codes: '47190,47710,47720,47910' },
];

const KINDS: Record<string, string> = {
  group: 'Group',
  full: 'Full',
  small: 'Small',
  'micro-entity': 'Micro',
  'total-exemption-full': 'Small (full)',
  'total-exemption-small': 'Small (abridged)',
  'audit-exemption-subsidiary': 'Subsidiary (audit exempt)',
  dormant: 'Dormant',
};

/** A filing's kind as a person would say it. */
export function kindLabel(kind: string): string {
  return KINDS[kind] ?? kind.replace(/-/g, ' ');
}

const field =
  'rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-100';

/** What importing a filing does: IAS accounting's store by default, another app's when given. */
export type CompaniesHouseImporter = (
  number: string,
  transactionId: string,
  name: string,
  options?: { reread?: boolean },
) => Promise<void>;

export default function CompaniesHouseImport({
  onImport,
  busy: busyOverride,
}: {
  onImport?: CompaniesHouseImporter;
  busy?: boolean;
} = {}) {
  const iasImport = useFinancialModelV2Store((s) => s.importFromCompaniesHouse);
  const iasBusy = useFinancialModelV2Store((s) => s.pdfProgress !== null);
  const importFromCompaniesHouse = onImport ?? iasImport;
  const busy = busyOverride ?? iasBusy;
  const [available, setAvailable] = useState<boolean | null>(null);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('active');
  const [type, setType] = useState('');
  const [sic, setSic] = useState('');
  const [hits, setHits] = useState<CompanyHit[] | null>(null);
  const [company, setCompany] = useState<CompanyHit | null>(null);
  const [filings, setFilings] = useState<AccountsFiling[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const [reread, setReread] = useState(false);

  useEffect(() => {
    void companiesHouseConfigured().then(setAvailable);
  }, []);

  if (available === false) {
    return (
      <p className="text-xs text-gray-500">
        Companies House search is not set up on this server (no API key).
      </p>
    );
  }
  if (available === null) return null;

  // A sector or company type is a filter, and filters use the advanced search, which
  // narrows by status too. A name alone uses the plain search, which ranks by name.
  const filtered = Boolean(type || sic);
  const canSearch = query.trim().length > 0 || filtered;

  async function runSearch() {
    setSearching(true);
    setError(null);
    setCompany(null);
    setFilings(null);
    try {
      setHits(await searchCompanies(query.trim(), filtered ? { status, type, sic } : {}));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSearching(false);
    }
  }

  async function choose(hit: CompanyHit) {
    setCompany(hit);
    setFilings(null);
    setError(null);
    try {
      setFilings(await companyAccounts(hit.number));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="mt-4 border-t border-gray-100 pt-4">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        From Companies House
      </h3>
      <form
        className="mt-2 flex flex-wrap gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (canSearch) void runSearch();
        }}
      >
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Company name or number"
          aria-label="Company name or number"
          className={`${field} min-w-0 flex-1`}
        />
        <Button type="submit" variant="primary" disabled={!canSearch || searching}>
          {searching ? 'Searching…' : 'Search'}
        </Button>
      </form>
      <div className="mt-2 flex flex-wrap gap-2 text-sm">
        <select aria-label="Sector" className={field} value={sic} onChange={(e) => setSic(e.target.value)}>
          {SECTORS.map((s) => (
            <option key={s.label} value={s.codes}>
              {s.label}
            </option>
          ))}
        </select>
        <select aria-label="Company status" className={field} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="active">Active</option>
          <option value="dissolved">Dissolved</option>
          <option value="">Any status</option>
        </select>
        <select aria-label="Company type" className={field} value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">Any type</option>
          <option value="ltd">Private limited</option>
          <option value="plc">Public limited (plc)</option>
          <option value="llp">LLP</option>
        </select>
      </div>

      {error && (
        <p role="alert" className="mt-3 text-sm text-red-700">
          {error}
        </p>
      )}

      {hits && !company && (
        <ul className="mt-3 max-h-64 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200">
          {hits.length === 0 && <li className="px-3 py-2 text-sm text-gray-500">No companies found.</li>}
          {hits.map((hit) => (
            <li key={hit.number}>
              <button
                type="button"
                onClick={() => void choose(hit)}
                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
              >
                <span className="font-medium text-gray-900">{hit.name}</span>{' '}
                <span className="text-gray-500">
                  {hit.number} · {hit.status}
                  {hit.address ? ` · ${hit.address}` : ''}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {company && (
        <div className="mt-3 rounded-lg border border-gray-200 p-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-gray-900">
              {company.name} <span className="font-normal text-gray-500">{company.number}</span>
            </p>
            <Button size="sm" onClick={() => setCompany(null)}>
              Back to results
            </Button>
          </div>
          {filings === null && !error && <p className="mt-2 text-sm text-gray-500">Loading its accounts…</p>}
          {filings && filings.length === 0 && (
            <p className="mt-2 text-sm text-gray-500">No accounts filed.</p>
          )}
          {filings && filings.length > 0 && (
            <>
              <ul className="mt-2 divide-y divide-gray-100">
                {filings.map((filing) => (
                  <li key={filing.transactionId} className="flex items-center justify-between gap-2 py-2 text-sm">
                    <span>
                      <span className="text-gray-900">
                        Year to {filing.madeUpTo || '—'} · {kindLabel(filing.kind)}
                      </span>{' '}
                      <span className="text-gray-500">
                        filed {filing.filed}
                        {filing.pages ? ` · ${filing.pages} pages` : ''}
                        {filing.formats.includes('ixbrl') ? ' · tagged (iXBRL)' : ''}
                      </span>
                    </span>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={busy || !filing.formats.includes('pdf')}
                      onClick={() =>
                        void importFromCompaniesHouse(
                          company.number,
                          filing.transactionId,
                          `${company.name} ${filing.madeUpTo}`.trim(),
                          { reread },
                        )
                      }
                    >
                      Import
                    </Button>
                  </li>
                ))}
              </ul>
              <div className="mt-2">
                <ReadAfresh checked={reread} onChange={setReread} disabled={busy} />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
